import {
  BitkitAlert,
  BitkitButton,
  BitkitIconButton,
  BitkitLink,
  BitkitPageSidebar,
  BitkitTour,
  IconArrowNortheast,
  IconBuild,
  IconBuildCache,
  IconClock,
  IconDownload,
  IconInsights,
  IconOpenInNew,
  IconPlay,
  IconSettings,
  IconTestQuarantine,
  IconWorkflow,
  useBitkitTour,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Group } from '@chakra-ui/react/group';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { ReactNode, useContext, useRef } from 'react';

import AppAvatar from '@/components/AppAvatar/AppAvatar';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import PageLayout from '@/components/page/PageLayout/PageLayout';
import { PageLayoutSidebarItem } from '@/components/page/PageLayout/PageLayoutSidebar';
import RestrictedPlanBuildUpsellDialog from '@/components/RestrictedPlanBuildUpsellDialog/RestrictedPlanBuildUpsellDialog';
import useRestrictedPlanBuildUpsell from '@/components/RestrictedPlanBuildUpsellDialog/useRestrictedPlanBuildUpsell';
import { CardVerificationContext } from '@/hooks/analytics/cardVerificationTracking';
import { getGitProviderIcon } from '@/models/GitProvider';
import {
  appSettingsPath,
  basePathForApp,
  billingPath,
  buildCacheInvocationListPath,
  ciOverviewPath,
  insightsExploreBuildsPath,
  installableArtifactsPath,
  projectInvocationsPath,
  projectTestQuarantinePage,
  workflowEditorPath,
} from '@/utils/monolithApiRouteService';

import { ScheduledBuild } from '../AppDetails/components/ScheduledBuilds/scheduledBuilds.types';
import useWorkflowInfoHighlight from '../AppDetails/hooks/useWorkflowInfoHighlight';
import WatchAppToggle from '../AppDetails/WatchAppToggle';
import AppDetailsPageProps from '../types/AppDetailsPageProps';
import AppPageProps from '../types/AppPageProps';
import AIAgentsCard from './AIAgentsCard';
import ScheduleBuildDialog from './ScheduleBuildDialog/ScheduleBuildDialog';
import StartBuildDialog from './StartBuildDialog/StartBuildDialog';

const CARD_VERIFICATION: CardVerificationContext = {
  source: 'app_details_page',
  reason: 'build_limit_reached',
  productName: 'ci',
};

export type GetSideBarItemsParams = {
  appSlug: string;
  isViewedAsPublicPage: boolean;
  canViewInternalPages: boolean;
};

export function getSideBarItems({
  appSlug,
  isViewedAsPublicPage,
  canViewInternalPages,
}: GetSideBarItemsParams): PageLayoutSidebarItem[] {
  const items: PageLayoutSidebarItem[] = [
    {
      itemProps: {
        href: basePathForApp(appSlug),
      },
      icon: IconBuild,
      label: 'Builds',
      pageName: 'Builds',
    },
  ];

  // The members-only pages stay hidden from the anonymous public view; a logged-in
  // non-member still sees them (Invocations disabled, since its backend needs internal_view).
  if (!isViewedAsPublicPage) {
    items.push({
      itemProps: {
        href: installableArtifactsPath(appSlug),
      },
      icon: IconDownload,
      label: 'Artifacts',
      pageName: 'Artifacts',
    });
    items.push({
      itemProps: {
        href: projectTestQuarantinePage(appSlug),
      },
      icon: IconTestQuarantine,
      label: 'Test quarantine',
      pageName: 'Test quarantine',
    });
    items.push({
      itemProps: {
        href: projectInvocationsPath(appSlug),
        disabled: !canViewInternalPages,
        disabledTooltip: canViewInternalPages
          ? undefined
          : 'Invocations are only available to members of this project. Ask a Workspace owner to invite you to the project team.',
      },
      icon: IconBuildCache,
      label: 'Invocations',
      pageName: 'Invocations',
    });
  }

  return items;
}

type AppPageLayoutProps = AppPageProps & {
  abilities: AppDetailsPageProps['abilities'];
  app: AppDetailsPageProps['app'];
  children: ReactNode;
  currentPage: 'Builds' | 'Artifacts' | 'Test quarantine' | 'Invocations';
  isViewedAsPublicPage: boolean;
  isWatchingApp: boolean;
  onStartOrScheduleBuildDialogClose?(): void;
  scheduledBuild?: ScheduledBuild;
  workflowsInfoShown: boolean;
};

export const AppPageLayout = ({
  abilities,
  app,
  children,
  ai,
  currentPage,
  isOwnerUser,
  isViewedAsPublicPage,
  isWatchingApp,
  onStartOrScheduleBuildDialogClose,
  scheduledBuild,
  shouldShowFailedPaymentAlert,
  workflowsInfoShown,
}: AppPageLayoutProps) => {
  const { account } = useContext(GlobalPropsContext);
  const { isMobile } = useResponsive();

  const { isWorkflowInfoVisible, onWorkflowInfoClose } = useWorkflowInfoHighlight(
    workflowsInfoShown,
    currentPage,
    app.hasAnyBuild,
  );

  const workflowsButtonRef = useRef<HTMLElement>(null);
  // The tour's step points at this button, so its `open` and the button's own render condition have
  // to be the same thing — Zag ends a targetless tour as `not-found`, which never reaches the
  // `dismissed` branch that persists the flag, and the user is asked again on every visit.
  const canRenderWorkflowsButton = !isViewedAsPublicPage && abilities.canChangeSettings;
  const isWorkflowsTourOpen = isWorkflowInfoVisible && !isMobile && canRenderWorkflowsButton;
  const workflowsTour = useBitkitTour({
    onStatusChange: ({ status }) => {
      // Resizing to mobile closes the tour through `open`, and BitkitTour reports that close as a
      // dismissal like any other — recording it would mark the tip as seen for a user who only made
      // their window narrower, and it never returns. This is still true when the user dismisses it
      // themselves, and false when the tour was closed for them.
      if (status === 'dismissed' && isWorkflowsTourOpen) {
        onWorkflowInfoClose();
      }
    },
    open: isWorkflowsTourOpen,
    steps: [
      {
        actions: [{ action: 'dismiss', label: 'Got it' }],
        description: 'Set up your Workflows or use the default ones to start running builds.',
        id: 'workflows',
        target: () => workflowsButtonRef.current,
        title: 'Explore Workflows & Pipelines',
      },
    ],
  });

  const {
    open: isStartBuildDialogOpen,
    onClose: onStartBuildDialogClose,
    onOpen: onStartBuildDialogOpen,
  } = useDisclosure();

  const {
    isUpsellDialogOpen,
    onUpsellDialogClose,
    organizationSlug,
    isLoading: isUpsellLoading,
    guardBuildAction,
  } = useRestrictedPlanBuildUpsell();

  const {
    open: isScheduleBuildDialogOpen,
    onClose: onScheduleBuildDialogClose,
    onOpen: onScheduleBuildDialogOpen,
  } = useDisclosure();

  const handleStartBuildDialogClose = () => {
    onStartBuildDialogClose();
    onStartOrScheduleBuildDialogClose?.();
  };

  const handleScheduleBuildDialogClose = () => {
    onScheduleBuildDialogClose();
    onStartOrScheduleBuildDialogClose?.();
  };

  const notifications = (shouldShowFailedPaymentAlert || app.isDisabled) && (
    <Box display="flex" flexDir="column" gap="16" marginBottom="16">
      {shouldShowFailedPaymentAlert && (
        <BitkitAlert
          variant="critical"
          messageText={
            <>
              Payment for this {app.ownerType === 'Organization' ? 'workspace' : "project's workspace"} is now overdue.
              To ensure continued access to Bitrise,{' '}
              {isOwnerUser ? (
                <>
                  please review your{' '}
                  <BitkitLink href={billingPath(app.ownerType, app.owner.slug)} textDecoration="underline" isExternal>
                    billing information.
                  </BitkitLink>
                </>
              ) : (
                `please contact ${
                  app.ownerType === 'Organization' ? app.owner.name : app.owner.username
                } to review the billing information.`
              )}
            </>
          }
        />
      )}
      {app.isDisabled && (
        <BitkitAlert
          variant="critical"
          messageText={
            <>
              This project has been disabled. You can re-enable it on the{' '}
              <BitkitLink href={appSettingsPath(app.slug, 'builds')} textDecoration="underline">
                Builds
              </BitkitLink>{' '}
              tab of the app settings to start building again.
            </>
          }
        />
      )}
    </Box>
  );

  const showAppSettingsButton =
    abilities.canChangeSettings || abilities.canAccessBaseTeamTab || Boolean(abilities.canManageBuildCache);
  const appSettingsPagePath = (() => {
    if (abilities.canChangeSettings) return appSettingsPath(app.slug, 'general');
    if (abilities.canAccessBaseTeamTab) return appSettingsPath(app.slug, 'team');
    return appSettingsPath(app.slug, 'cache');
  })();

  const workflowButton = (isSticky: boolean) =>
    canRenderWorkflowsButton && (
      <BitkitButton
        ref={workflowsButtonRef}
        variant="secondary"
        href={workflowEditorPath(app.slug)}
        size={isSticky ? 'sm' : 'md'}
        icon={IconWorkflow}
        data-intercom-target="Workflows button"
      >
        Workflows
      </BitkitButton>
    );

  // Not a BitkitSplitButton: its trailing half is always a menu trigger, and this one opens the
  // schedule dialog directly.
  const startBuildButton = (isSticky: boolean) =>
    abilities.canRunBuilds && (
      <Group attached>
        <BitkitButton
          size={isSticky ? 'sm' : 'md'}
          flex="1"
          icon={IconPlay}
          state={isUpsellLoading ? 'loading' : undefined}
          onClick={() => guardBuildAction(onStartBuildDialogOpen)}
        >
          Start build
        </BitkitButton>
        <BitkitIconButton
          // The seam between two primary buttons is invisible without it, which is why both v1's
          // attached ButtonGroup and BitkitSplitButton draw this same line.
          borderInlineStartColor="border/on-contrast"
          icon={IconClock}
          label="Schedule builds"
          onClick={onScheduleBuildDialogOpen}
          size={isSticky ? 'sm' : 'md'}
        />
      </Group>
    );

  const buttons = (isSticky: boolean) => (
    <>
      {showAppSettingsButton && (
        <BitkitButton
          aria-label="Project Settings"
          href={appSettingsPagePath}
          icon={IconSettings}
          size={isSticky ? 'sm' : 'md'}
          variant="secondary"
        >
          Project settings
        </BitkitButton>
      )}
      {workflowButton(isSticky)}
      {startBuildButton(isSticky)}
    </>
  );

  return (
    <PageLayout
      footerBackground={currentPage === 'Builds' ? 'background/primary' : undefined}
      header={{
        breadcrumbData: [
          {
            key: '1',
            children: 'Bitrise CI',
            href: ciOverviewPath(),
          },
        ],
        avatar: <AppAvatar name={app.title} projectType={app.projectType} size="40" src={app.avatarUrl} />,
        children: ({ isSticky }) =>
          isMobile ? (
            // One row: the header lays each child out full width on mobile, so a separate settings
            // button came out as a lone icon stretched across its own row. The two actions share the
            // width; the icon keeps its size at the end, where master's sat beside the title.
            <Box display="flex" gap="12">
              {!isViewedAsPublicPage && (
                <Box css={{ '& > *': { flex: 1 } }} display="flex" flex="1" gap="12">
                  {workflowButton(isSticky)}
                  {startBuildButton(isSticky)}
                </Box>
              )}
              {showAppSettingsButton && (
                <BitkitIconButton
                  href={appSettingsPagePath}
                  icon={IconSettings}
                  label="App settings"
                  // The row's own size -- the default `lg` stood 8 taller than the buttons beside it.
                  size="md"
                  variant="secondary"
                />
              )}
            </Box>
          ) : isViewedAsPublicPage ? null : (
            buttons(isSticky)
          ),
        extraContent: notifications,
        title: app.title,
      }}
      isMobile={isMobile}
      platformShell={
        isViewedAsPublicPage
          ? {
              navWidth: 0,
              headerHeight: 16,
            }
          : undefined
      }
      sidebar={{
        items: getSideBarItems({
          appSlug: app.slug,
          isViewedAsPublicPage,
          canViewInternalPages: abilities.canViewInternalPages,
        }),
        extraContent: ai ? (
          <Box marginTop="16">
            <AIAgentsCard ai={ai} slug={app.slug} canChangeSettings={abilities.canChangeSettings} />
          </Box>
        ) : undefined,
        selectedPage: currentPage,
        footer: (
          <>
            {/* Outside the `Footer` and above its rule, because the design reads this as the last
                row of the navigation rather than one of the external links below it. `marginBottom`
                is the half of the gap to the rule that `BitkitPageSidebar.Body` carries for the
                list proper -- the `Footer` brings the other half. */}
            {!isViewedAsPublicPage && abilities.canWatch && (
              <WatchAppToggle appSlug={app.slug} isInitialWatching={isWatchingApp} marginBottom="8" />
            )}
            <BitkitPageSidebar.Footer>
              {account && !isViewedAsPublicPage && (
                <BitkitPageSidebar.Item
                  href={buildCacheInvocationListPath(account.slug, {
                    params: { project_slug: app.slug },
                  })}
                  icon={IconBuildCache}
                  rel="noopener noreferrer"
                  suffixIcon={IconArrowNortheast}
                  target="_blank"
                >
                  Build Cache
                </BitkitPageSidebar.Item>
              )}
              {account && !isViewedAsPublicPage && (
                <BitkitPageSidebar.Item
                  href={insightsExploreBuildsPath(account.slug, {
                    params: { ref: 'subheader-cta', app_slug: app.slug },
                  })}
                  icon={IconInsights}
                  rel="noopener noreferrer"
                  suffixIcon={IconArrowNortheast}
                  target="_blank"
                >
                  Insights
                </BitkitPageSidebar.Item>
              )}
              <BitkitPageSidebar.Item
                href={app.repositoryUrl}
                icon={getGitProviderIcon(app.provider)}
                rel="noopener noreferrer"
                suffixIcon={IconOpenInNew}
                target="_blank"
              >
                Source
              </BitkitPageSidebar.Item>
            </BitkitPageSidebar.Footer>
          </>
        ),
      }}
    >
      {children}
      {!isViewedAsPublicPage && (
        <>
          <StartBuildDialog
            app={app}
            buildTriggerToken={app.buildTriggerToken}
            defaultBranch={app.defaultBranch}
            isOpen={isStartBuildDialogOpen}
            onClose={handleStartBuildDialogClose}
            openScheduleBuildDialog={onScheduleBuildDialogOpen}
          />
          <ScheduleBuildDialog
            app={app}
            buildTriggerToken={app.buildTriggerToken}
            defaultBranch={app.defaultBranch}
            isOpen={isScheduleBuildDialogOpen || Boolean(scheduledBuild)}
            onClose={handleScheduleBuildDialogClose}
            scheduledBuild={scheduledBuild}
          />
          <RestrictedPlanBuildUpsellDialog
            isOpen={isUpsellDialogOpen}
            onClose={onUpsellDialogClose}
            organizationSlug={organizationSlug}
            cardVerification={CARD_VERIFICATION}
          />
          <BitkitTour tour={workflowsTour} />
        </>
      )}
    </PageLayout>
  );
};
